import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Separator } from '../../components/ui/separator';
import {
  ArrowLeft,
  Pencil,
  Trash2,
  Server,
  Code,
  Database,
  Cloud,
  Users,
  Building,
  MapPin,
  Calendar,
  Shield,
  Link as LinkIcon,
  AlertTriangle,
  FileText,
  ListTodo,
  Bug,
  History,
  Loader2
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { assetsApi, tasksApi, evidenceApi, vulnerabilitiesApi } from '../../lib/api';
import { toast } from 'sonner';
import { cn, getCriticalityBadge, getAssetTypeConfig, getStatusConfig, formatDate, formatDateTime } from '../../lib/utils';

const assetTypeIcons = {
  hardware: Server,
  software: Code,
  dados: Database,
  servico: Cloud,
  pessoas: Users,
  instalacao: Building,
};

export default function AssetDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [asset, setAsset] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showDelete, setShowDelete] = useState(false);
  const [relatedTasks, setRelatedTasks] = useState([]);
  const [relatedEvidence, setRelatedEvidence] = useState([]);
  const [relatedVulns, setRelatedVulns] = useState([]);

  useEffect(() => {
    loadAsset();
  }, [id]);

  const loadAsset = async () => {
    setIsLoading(true);
    try {
      const response = await assetsApi.getById(id);
      setAsset(response.data);
      
      // Load related tasks
      const tasksResponse = await tasksApi.getAll({ asset_id: id });
      setRelatedTasks(tasksResponse.data);
      
      // Load related evidence
      const evidenceResponse = await evidenceApi.getAll({ asset_id: id });
      setRelatedEvidence(evidenceResponse.data);

      // Load related vulnerabilities
      const vulnsResponse = await vulnerabilitiesApi.getAll({ asset_id: id });
      setRelatedVulns(vulnsResponse.data);
    } catch (error) {
      toast.error('Erro ao carregar ativo');
      navigate('/assets');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await assetsApi.delete(id);
      toast.success('Ativo eliminado com sucesso');
      navigate('/assets');
    } catch (error) {
      toast.error('Erro ao eliminar ativo');
    }
  };

  if (isLoading) {
    return (
      <Layout title="Detalhes do Ativo">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </Layout>
    );
  }

  if (!asset) {
    return null;
  }

  const Icon = assetTypeIcons[asset.asset_type] || Server;
  const typeConfig = getAssetTypeConfig(asset.asset_type);
  const criticalityConfig = getCriticalityBadge(asset.criticality);
  const statusConfig = getStatusConfig(asset.operational_status);

  const headerContent = (
    <div className="flex items-center gap-2">
      <Button variant="outline" size="sm" onClick={() => navigate(`/assets/${id}/edit`)} data-testid="edit-asset-btn">
        <Pencil className="h-4 w-4 mr-2" />
        Editar
      </Button>
      <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700" onClick={() => setShowDelete(true)} data-testid="delete-asset-btn">
        <Trash2 className="h-4 w-4 mr-2" />
        Eliminar
      </Button>
    </div>
  );

  return (
    <Layout 
      title={
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/assets')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <span>{asset.name}</span>
        </div>
      }
      headerContent={headerContent}
    >
      <div className="space-y-6" data-testid="asset-detail-container">
        {/* Header Card */}
        <Card className="card-flat">
          <CardContent className="p-6">
            <div className="flex items-start gap-6">
              <div className={cn('h-16 w-16 rounded-lg flex items-center justify-center bg-slate-100', typeConfig.color)}>
                <Icon className="h-8 w-8" />
              </div>
              <div className="flex-1">
                <div className="flex items-start justify-between">
                  <div>
                    <h1 className="text-2xl font-bold text-slate-900 font-heading">{asset.name}</h1>
                    <p className="text-sm text-slate-500 font-mono">{asset.asset_id}</p>
                    {asset.description && (
                      <p className="text-sm text-slate-600 mt-2 max-w-2xl">{asset.description}</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Badge variant="outline" className={cn('text-sm', criticalityConfig.color)}>
                      {criticalityConfig.label}
                    </Badge>
                    <Badge variant="outline" className={cn('text-sm', statusConfig.color)}>
                      {statusConfig.label}
                    </Badge>
                  </div>
                </div>
                <div className="flex flex-wrap gap-6 mt-4 text-sm text-slate-600">
                  <div className="flex items-center gap-2">
                    <Server className="h-4 w-4 text-slate-400" />
                    <span>{typeConfig.label}</span>
                  </div>
                  {asset.department && (
                    <div className="flex items-center gap-2">
                      <Building className="h-4 w-4 text-slate-400" />
                      <span>{asset.department}</span>
                    </div>
                  )}
                  {asset.physical_location && (
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-slate-400" />
                      <span>{asset.physical_location}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-slate-400" />
                    <span>Criado em {formatDate(asset.created_at)}</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="info" className="space-y-4">
          <TabsList>
            <TabsTrigger value="info" data-testid="tab-info">Informação Geral</TabsTrigger>
            <TabsTrigger value="security" data-testid="tab-security">Classificação</TabsTrigger>
            <TabsTrigger value="technical" data-testid="tab-technical">Técnico</TabsTrigger>
            <TabsTrigger value="tasks" data-testid="tab-tasks">Tarefas ({relatedTasks.length})</TabsTrigger>
            <TabsTrigger value="vulnerabilities" data-testid="tab-vulnerabilities">Vulnerabilidades ({relatedVulns.length})</TabsTrigger>
            <TabsTrigger value="evidence" data-testid="tab-evidence">Evidências ({relatedEvidence.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="info">
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="card-flat">
                <CardHeader>
                  <CardTitle className="text-lg">Propriedade e Responsabilidade</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-slate-500">Proprietário</p>
                      <p className="font-medium">{asset.owner_id || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Gestor Técnico</p>
                      <p className="font-medium">{asset.technical_manager_id || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Custodian</p>
                      <p className="font-medium">{asset.custodian_id || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Departamento</p>
                      <p className="font-medium">{asset.department || '-'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-flat">
                <CardHeader>
                  <CardTitle className="text-lg">Localização</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-slate-500">Localização Física</p>
                      <p className="font-medium">{asset.physical_location || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Localização Lógica</p>
                      <p className="font-medium">{asset.logical_location || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Endereço IP</p>
                      <p className="font-medium font-mono">{asset.ip_address || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Hostname</p>
                      <p className="font-medium font-mono">{asset.hostname || '-'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="security">
            <Card className="card-flat">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Shield className="h-5 w-5 text-blue-600" />
                  Classificação de Segurança (CIA)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-4 gap-6">
                  <div className="text-center p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-500 mb-2">Confidencialidade</p>
                    <Badge variant="outline" className={cn('text-sm', getCriticalityBadge(asset.confidentiality).color)}>
                      {asset.confidentiality}
                    </Badge>
                  </div>
                  <div className="text-center p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-500 mb-2">Integridade</p>
                    <Badge variant="outline" className={cn('text-sm', getCriticalityBadge(asset.integrity).color)}>
                      {asset.integrity}
                    </Badge>
                  </div>
                  <div className="text-center p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-500 mb-2">Disponibilidade</p>
                    <Badge variant="outline" className={cn('text-sm', getCriticalityBadge(asset.availability).color)}>
                      {asset.availability}
                    </Badge>
                  </div>
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-600 mb-2">Criticidade Global</p>
                    <Badge variant="outline" className={cn('text-sm font-bold', criticalityConfig.color)}>
                      {criticalityConfig.label}
                    </Badge>
                  </div>
                </div>
                {asset.estimated_value && (
                  <div className="mt-6 p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-500">Valor Estimado</p>
                    <p className="text-2xl font-bold text-slate-900">
                      {new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(asset.estimated_value)}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="technical">
            <Card className="card-flat">
              <CardHeader>
                <CardTitle className="text-lg">Informação Técnica</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-6">
                  <div>
                    <p className="text-sm text-slate-500">Fabricante/Fornecedor</p>
                    <p className="font-medium">{asset.vendor || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Modelo</p>
                    <p className="font-medium">{asset.model || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Versão</p>
                    <p className="font-medium">{asset.version || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Número de Série</p>
                    <p className="font-medium font-mono">{asset.serial_number || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Data de Aquisição</p>
                    <p className="font-medium">{formatDate(asset.acquisition_date)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Fim de Vida</p>
                    <p className="font-medium">{formatDate(asset.end_of_life)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Fim de Suporte</p>
                    <p className="font-medium">{formatDate(asset.end_of_support)}</p>
                  </div>
                </div>
                
                {asset.tags && asset.tags.length > 0 && (
                  <div className="mt-6">
                    <p className="text-sm text-slate-500 mb-2">Tags</p>
                    <div className="flex flex-wrap gap-2">
                      {asset.tags.map((tag, index) => (
                        <Badge key={index} variant="secondary">{tag}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                
                {asset.notes && (
                  <div className="mt-6">
                    <p className="text-sm text-slate-500 mb-2">Notas</p>
                    <p className="text-sm text-slate-700 bg-slate-50 p-4 rounded-lg">{asset.notes}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="tasks">
            <Card className="card-flat">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <ListTodo className="h-5 w-5 text-amber-600" />
                  Tarefas Relacionadas
                </CardTitle>
                <Button size="sm" onClick={() => navigate(`/tasks/new?asset_id=${id}`)}>
                  Criar Tarefa
                </Button>
              </CardHeader>
              <CardContent>
                {relatedTasks.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-8">
                    Nenhuma tarefa associada a este ativo
                  </p>
                ) : (
                  <div className="space-y-3">
                    {relatedTasks.map((task) => (
                      <div key={task.task_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div>
                          <p className="font-medium text-slate-900">{task.title}</p>
                          <p className="text-sm text-slate-500">{task.task_id}</p>
                        </div>
                        <Badge variant="outline">{task.status}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="vulnerabilities">
            <Card className="card-flat">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Bug className="h-5 w-5 text-red-600" />
                  Vulnerabilidades do Ativo
                </CardTitle>
                <Button size="sm" variant="outline" onClick={() => navigate('/vulnerabilities')}>
                  Ver todas
                </Button>
              </CardHeader>
              <CardContent>
                {relatedVulns.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-8" data-testid="no-vulns-msg">
                    Sem vulnerabilidades registadas para este ativo
                  </p>
                ) : (
                  <div className="space-y-3" data-testid="asset-vulns-list">
                    {relatedVulns.map((v) => {
                      const sevColors = {
                        critica: 'bg-red-50 text-red-700 border-red-200',
                        alta: 'bg-orange-50 text-orange-700 border-orange-200',
                        media: 'bg-yellow-50 text-yellow-700 border-yellow-200',
                        baixa: 'bg-blue-50 text-blue-700 border-blue-200',
                        informativa: 'bg-slate-50 text-slate-700 border-slate-200',
                      };
                      const stColors = {
                        nova: 'bg-red-50 text-red-700 border-red-200',
                        confirmada: 'bg-orange-50 text-orange-700 border-orange-200',
                        em_remediacao: 'bg-blue-50 text-blue-700 border-blue-200',
                        remediada: 'bg-green-50 text-green-700 border-green-200',
                        aceite: 'bg-slate-50 text-slate-700 border-slate-200',
                        falso_positivo: 'bg-slate-50 text-slate-500 border-slate-200',
                      };
                      return (
                        <Link
                          key={v.vulnerability_id}
                          to={`/vulnerabilities?focus=${v.vulnerability_id}`}
                          className="block p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                          data-testid={`asset-vuln-${v.vulnerability_id}`}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <span className="font-mono text-xs text-slate-400">{v.vulnerability_id}</span>
                                {v.cve_id && (
                                  <Badge variant="outline" className="text-xs font-mono bg-white">{v.cve_id}</Badge>
                                )}
                                <Badge variant="outline" className={cn('text-xs', sevColors[v.severity])}>
                                  {v.severity}
                                </Badge>
                                <Badge variant="outline" className={cn('text-xs', stColors[v.status])}>
                                  {v.status}
                                </Badge>
                                {v.cvss_score != null && (
                                  <Badge variant="outline" className="text-xs bg-white">CVSS {v.cvss_score}</Badge>
                                )}
                              </div>
                              <p className="font-medium text-slate-900 truncate">{v.title}</p>
                              {v.affected_component && (
                                <p className="text-xs text-slate-500 mt-1 font-mono">{v.affected_component}</p>
                              )}
                            </div>
                            <span className="text-xs text-slate-500 shrink-0">{formatDate(v.discovered_at)}</span>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="evidence">
            <Card className="card-flat">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="h-5 w-5 text-green-600" />
                  Evidências Associadas
                </CardTitle>
                <Button size="sm" onClick={() => navigate(`/evidence/new?asset_id=${id}`)}>
                  Adicionar Evidência
                </Button>
              </CardHeader>
              <CardContent>
                {relatedEvidence.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-8">
                    Nenhuma evidência associada a este ativo
                  </p>
                ) : (
                  <div className="space-y-3">
                    {relatedEvidence.map((ev) => (
                      <div key={ev.evidence_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div>
                          <p className="font-medium text-slate-900">{ev.name}</p>
                          <p className="text-sm text-slate-500">{ev.evidence_type}</p>
                        </div>
                        <span className="text-sm text-slate-500">{formatDate(ev.created_at)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Delete Dialog */}
      <AlertDialog open={showDelete} onOpenChange={setShowDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar Ativo</AlertDialogTitle>
            <AlertDialogDescription>
              Tem a certeza que pretende eliminar o ativo "{asset.name}"?
              Esta ação não pode ser revertida.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={handleDelete}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
